import { Messages } from 'primereact/messages';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../../../utils/errors';
import { getApiClient } from '../../../api';
import type { LanguageAddRequest, LanguageEditRequest, LanguageResponse } from '../../../api';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { ProgressSpinner } from '../../../components/PrimereactComponents';
import { LanguageTable } from './components/LanguageTable';
import { ButtonAdd } from '../../components/Buttons';
import { LanguageDialog } from './components/LanguageDialog';
import { DeleteLanguageDialog } from './components/DeleteLanguageDialog';

export default function Languages() {
    const [languages, setLanguages] = useState<LanguageResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    const [showLanguageDialog, setShowLanguageDialog] = useState<boolean>(false);
    const [showDeleteLanguageDialog, setShowDeleteLanguageDialog] = useState<boolean>(false);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageResponse | null>(null);

    const msgs = useRef<Messages>(null!);
    const refLanguageDialogMsgs = useRef<Messages>(null!);

    const loadLanguages = useCallback(async (): Promise<void> => {
        try {
            const response = await getApiClient().getAdminLanguage();
            setLanguages(response);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se načíst jazyky');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLanguages();
    }, [loadLanguages]);

    const handleClickAddLanguage = (): void => {
        setSelectedLanguage(null);
        setShowLanguageDialog(true);
    };

    const handleClickEditLanguage = (rowData: LanguageResponse): void => {
        setSelectedLanguage(rowData);
        setShowLanguageDialog(true);
    };

    const hideLanguageDialog = (): void => {
        if (processing) {
            return;
        }
        setShowLanguageDialog(false);
        setSelectedLanguage(null);
    };

    const handleClickDeleteLanguage = (rowData: LanguageResponse): void => {
        setSelectedLanguage(rowData);
        setShowDeleteLanguageDialog(true);
    };

    const hideDeleteLanguageDialog = (): void => {
        if (processing) {
            return;
        }
        setShowDeleteLanguageDialog(false);
        setSelectedLanguage(null);
    };

    const handleAddLanguage = async (data: LanguageAddRequest): Promise<void> => {
        setProcessing(true);
        try {
            await getApiClient().postAdminLanguage(data);
            showSuccessMessage(msgs, `Jazyk ${data.en_name} byl úspěšně vytvořen`);
            await loadLanguages();
            hideLanguageDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refLanguageDialogMsgs, errorMessage ?? 'Nepodařilo se vytvořit jazyk', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    const handleEditLanguage = async (data: LanguageEditRequest): Promise<void> => {
        if (!selectedLanguage) return;

        setProcessing(true);
        try {
            await getApiClient().putAdminLanguageByLanguageCode(selectedLanguage.language_code, data);
            showSuccessMessage(msgs, `Jazyk byl úspěšně upraven`);
            await loadLanguages();
            hideLanguageDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refLanguageDialogMsgs, errorMessage ?? 'Nepodařilo se upravit jazyk', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteLanguage = async (): Promise<void> => {
        if (!selectedLanguage) return;

        setProcessing(true);
        try {
            await getApiClient().deleteAdminLanguageByLanguageCode(selectedLanguage.language_code);
            showSuccessMessage(msgs, `Jazyk ${selectedLanguage.language_code.toUpperCase()} byl úspěšně smazán`);
            await loadLanguages();
            hideDeleteLanguageDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se smazat jazyk', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <>
            <h1>Jazyky</h1>

            <div className='page__buttons'>
                <ButtonAdd
                    label='Přidat jazyk'
                    onClick={handleClickAddLanguage}
                    disabled={processing}
                />
                {processing && <ProgressSpinner />}
            </div>

            <Messages ref={msgs} className='page__messages' />

            <LanguageTable 
                languages={languages}
                loading={loading} 
                onClickEditLanguage={handleClickEditLanguage}
                onClickDeleteLanguage={handleClickDeleteLanguage}
                disabled={processing}
            />

            <DeleteLanguageDialog
                isVisible={showDeleteLanguageDialog}
                onHide={hideDeleteLanguageDialog}
                handleDeleteLanguage={handleDeleteLanguage}
                language={selectedLanguage}
                processing={processing}
            />

            <LanguageDialog
                isVisible={showLanguageDialog}
                onHide={hideLanguageDialog}
                onSubmitAdd={handleAddLanguage}
                onSubmitEdit={handleEditLanguage}
                editLanguage={selectedLanguage}
                msgs={refLanguageDialogMsgs}
                processing={processing}
            />
        </>
    );
}
